import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, Link, useOutletContext } from 'react-router-dom';
import './SentencePracticePage.css';
import { backendUrl } from '../config';
import Spinner from './Spinner';
import OnScreenKeyboard from './OnScreenKeyboard';
import { useLanguage } from '../contexts/LanguageContext';
import { getKeyboardLangCode } from '../utils/keyboardUtils';
import { isRTL as isRTLanguage } from '../utils/rtlUtils';
import { supabase } from '../supabaseClient';
import { getUserTimezone } from '../utils/timezoneUtils';
import { useModuleData } from '../hooks/useModuleData';
import { useWordGroupData } from '../hooks/useWordGroupData';
import { useWordsData } from '../hooks/useWordsData';
import { cacheUtils } from '../utils/cacheManager';



// General normalization that removes vowels and diacritics for any language
const normalizeAnswerWithVowels = (str, language) => {
  if (!str) return '';

  // Always normalize to remove diacritics and vowels for all languages
  // This makes the grading more flexible and user-friendly
  return str
    .toLowerCase()
    .normalize('NFD') // Decompose characters into base + diacritics
    .replace(/[\u0300-\u036f]/g, '') // Remove all combining diacritical marks (vowels, accents, etc.)
    .replace(/[\u0591-\u05C7]/g, '') // Remove Hebrew diacritics (nikud)
    .replace(/[\u064B-\u065F\u0670-\u06D6\u06D7-\u06ED]/g, '') // Remove Arabic diacritics (tashkeel)
    .replace(/[\uFB1D-\uFB4F]/g, '') // Remove Hebrew presentation forms
    .replace(/[\uFE70-\uFEFF]/g, '') // Remove Arabic presentation forms
    .replace(/[\u1AB0-\u1AFF]/g, '') // Remove combining diacritical marks extended
    .replace(/[\u20D0-\u20FF]/g, '') // Remove combining diacritical marks for symbols
    .replace(/[\uFE20-\uFE2F]/g, '') // Remove combining half marks
    .replace(/[.,/#!$%^&*;:{}=\-_`~()?'"¡¿]/g, '') // Remove punctuation
    .trim();
};

const normalizePinyin = (str) => {
  if (!str) return '';
  // Remove diacritics, spaces, and lowercase
  const normalized = str
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // Remove tone marks
    .replace(/[^a-z0-9]/g, '') // Remove everything except letters and numbers
    .trim();

  return normalized;
};

const SentencePracticePage = () => {
  const { t } = useLanguage();
  const { id, groupId } = useParams();
  const { refetchUsage } = useOutletContext() || {};
  const navigate = useNavigate();

  // Use DataContext hooks
  const { moduleData, loading: moduleLoading } = useModuleData(id);
  const { groupData, loading: groupLoading } = useWordGroupData(id, groupId);
  const { words, loading: wordsLoading } = useWordsData(groupData?.id);

  // Debug logging


  const dataLoading = moduleLoading || groupLoading || wordsLoading;

  const [sentences, setSentences] = useState([]);
  const [userInputs, setUserInputs] = useState([]);
  const [feedback, setFeedback] = useState([]);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const hasFetched = useRef(false);
  const requestIdRef = useRef(0);

  // Keyboard state
  const [keyboardArmedLang, setKeyboardArmedLang] = useState(null);
  const [keyboardActiveIdx, setKeyboardActiveIdx] = useState(null);
  const [focusedInputIdx, setFocusedInputIdx] = useState(null);
  const keyboardOverlayRef = useRef(null);
  const sentenceInputRefs = useRef([]);

  const [showHints, setShowHints] = useState([]);
  const [wrappedInputs, setWrappedInputs] = useState(new Set());



  // Function to check if input is on its own line
  const checkIfInputWrapped = useCallback((index) => {
    const input = sentenceInputRefs.current[index];
    if (!input) return false;

    const inputRect = input.getBoundingClientRect();
    const sentenceText = input.closest('.sentence-text');
    if (!sentenceText) return false;

    const sentenceRect = sentenceText.getBoundingClientRect();

    // Check if input is on its own line (input top is significantly below sentence top)
    const isWrapped = inputRect.top > sentenceRect.top + 20; // 20px threshold

    setWrappedInputs(prev => {
      const newSet = new Set(prev);
      if (isWrapped) {
        newSet.add(index);
      } else {
        newSet.delete(index);
      }
      return newSet;
    });

    return isWrapped;
  }, []);



  const fetchSentences = useCallback(async () => {
    setLoading(true);
    setError('');
    setIsSubmitted(false);
    setSentences([]);
    const currentRequestId = ++requestIdRef.current;
    try {
      const shuffledWords = [...words].sort(() => 0.5 - Math.random());
      const selectedWords = shuffledWords.slice(0, 10);
      // Format words for backend - ensure we send the actual word text, not IDs
      const formattedWords = selectedWords.map(word => ({
        from_text: word.from_text,
        to_text: word.to_text
      }));
      // Get the current user's access token
      const { data: { session } } = await supabase.auth.getSession();
      const accessToken = session?.access_token;
      const res = await fetch(`${backendUrl}/gemini-generate-sentences`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          fromLang: moduleData.from_lang,
          toLang: moduleData.to_lang,
          words: formattedWords,
          dialect: moduleData.dialect,
          accessToken,
          timezone: getUserTimezone(),
          practiceType: 'fib'
        }),
      });
      if (!res.ok) {
        const data = await res.json();
        if (requestIdRef.current === currentRequestId) setError(data.error || t('aiCouldNotGenerateSentences'));
        return;
      }
      const data = await res.json();
      if (data.sentences && data.sentences.length > 0) {
        if (requestIdRef.current === currentRequestId) {

          cacheUtils.cacheSentences(moduleData.id, groupData.id, 'fib', data.sentences);
          setSentences(data.sentences);
          setUserInputs(Array(data.sentences.length).fill(''));
          setFeedback(Array(data.sentences.length).fill(null));
          setShowHints(Array(data.sentences.length).fill(false));
          if (refetchUsage) refetchUsage();
        }
      } else {
        if (requestIdRef.current === currentRequestId) setError(data.error || 'The AI could not generate sentences for this word group. Please try another one.');
      }
    } catch (error) {
      if (requestIdRef.current === currentRequestId) setError(t('couldNotConnectToAI'));
    } finally {
      if (requestIdRef.current === currentRequestId) setLoading(false);
    }
  }, [words, moduleData?.from_lang, moduleData?.to_lang, moduleData?.dialect, moduleData?.id, groupData?.id, t, refetchUsage]);

  useEffect(() => {
    if (!hasFetched.current && words && words.length > 0 && moduleData?.id && groupData?.id) {
      // Try to get cached sentences from cacheManager
      const cachedSentences = cacheUtils.getCachedSentences(moduleData.id, groupData.id, 'fib');

      if (cachedSentences && Array.isArray(cachedSentences)) {
        setSentences(cachedSentences);
        setUserInputs(Array(cachedSentences.length).fill(''));
        setFeedback(Array(cachedSentences.length).fill(null));
        setShowHints(Array(cachedSentences.length).fill(false));
        setLoading(false);
        hasFetched.current = true;
      } else {
        // If no valid cache, fetch and cache new sentences
        hasFetched.current = true;
        fetchSentences();
      }
    }
  }, [words, fetchSentences, moduleData?.id, groupData?.id]);

  // Check for wrapped inputs on window resize
  useEffect(() => {
    const handleResize = () => {
      wrappedInputs.forEach(index => {
        checkIfInputWrapped(index);
      });
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [wrappedInputs, checkIfInputWrapped]);

  // Check for wrapped inputs when sentences change
  useEffect(() => {
    if (sentences.length > 0) {
      // Small delay to ensure DOM is rendered
      setTimeout(() => {
        wrappedInputs.forEach(index => {
          checkIfInputWrapped(index);
        });
      }, 100);
    }
  }, [sentences, wrappedInputs, checkIfInputWrapped]);

  const handleInputChange = (index, value) => {
    const newInputs = [...userInputs];
    newInputs[index] = value;
    setUserInputs(newInputs);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!sentences || !Array.isArray(sentences)) {
      console.error('Sentences not available for submission');
      return;
    }
    const newFeedback = sentences.map((sentence, index) => {
      let correctAnswers = sentence.correct_word.split(',').map(word => normalizeAnswerWithVowels(word, moduleData.to_lang));
      let userAnswer = normalizeAnswerWithVowels(userInputs[index], moduleData.to_lang);
      let isCorrect = correctAnswers.includes(userAnswer);

      // Special logic for Mandarin/Cantonese: accept either Hanzi or romanization (pinyin/jyutping)
      const isChineseTarget = ['Mandarin', 'zh', 'zh-CN', 'Cantonese', 'yue', 'zh-hk', 'zh-yue'].includes(moduleData.to_lang);
      if (!isCorrect && isChineseTarget) {
        const correctWord = sentence.correct_word.trim();
        const userInput = userInputs[index].trim();



        // First, check if user input matches the Hanzi exactly (after normalization)
        if (normalizeAnswerWithVowels(userInput, moduleData.to_lang) === normalizeAnswerWithVowels(correctWord, moduleData.to_lang)) {
          isCorrect = true;
        }
        // If not, check if user input is pinyin (contains only Latin letters/numbers)
        else if (/^[a-zA-Z0-9\s]+$/.test(userInput)) {
          // Look up the word in the original word list to find its pinyin


          // Find the word by extracting Hanzi part from word list entries
          const originalWord = words.find(w => {
            const wordText = w.to_text;
            const hanziMatch = wordText.match(/^([^()]+)(?:\s*\(([^)]+)\))?/);
            const hanziPart = hanziMatch ? hanziMatch[1].trim() : wordText;
            return hanziPart === correctWord;
          });


          if (originalWord) {
            // Check if the word has pinyin in parentheses format: "汉字 (hànzì)"
            const wordText = originalWord.to_text;
            const pinyinMatch = wordText.match(/^([^()]+)(?:\s*\(([^)]+)\))?/);



            if (pinyinMatch && pinyinMatch[2]) {
              // Word has pinyin, compare with user input
              const pinyin = pinyinMatch[2].trim();
              const normalizedUserPinyin = normalizePinyin(userInput);
              const normalizedWordPinyin = normalizePinyin(pinyin);



              if (normalizedUserPinyin === normalizedWordPinyin) {
                isCorrect = true;
              } else {

              }
            } else {
              // Word doesn't have pinyin, try to match user input as pinyin against the Hanzi
              // This is a fallback for when words don't include pinyin

            }
          } else {

          }
        }


      }

      if (isCorrect) {
        return { status: 'correct', message: t('correct') };
      } else {
        return { status: 'incorrect', message: `${t('incorrect')} ${sentence.correct_word}` };
      }
    });
    setFeedback(newFeedback);
    setIsSubmitted(true);

    // Clear the cache for this specific practice type after grading
    if (moduleData?.id && groupData?.id) {
      cacheUtils.clearSentencesCache(moduleData.id, groupData.id, 'fib');
    }
  };

  const handlePracticeAgain = () => {
    // Clear the cache for this specific practice type
    cacheUtils.clearSentencesCache(moduleData.id, groupData.id, 'fib');
    hasFetched.current = false;
    fetchSentences();
  };

  if (dataLoading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '70vh',
        width: '100%',
        flexDirection: 'column',
      }}>
        <Spinner message={t('loading')} size="large" />
      </div>
    );
  }

  if (!moduleData || !groupData) {
    return (
      <div className="card" style={{ textAlign: 'center', padding: '2rem' }}>
        <h2>{t('wordGroupNotFound')}</h2>
        <p>{t('wordGroupNotFoundMessage')}</p>
        <button className="btn btn-primary" onClick={() => navigate(-1)}>{t('backToModules')}</button>
      </div>
    );
  }

  if (words && words.length === 0) {
    return (
      <div className="card" style={{ textAlign: 'center', padding: '2rem' }}>
        <h2>{t('noWordsYet')}</h2>
        <p>{t('addYourFirstWords')}</p>
        <button className="btn btn-primary" onClick={() => navigate(`/module/${moduleData.id}/group/${groupData.id}`)}>{t('backToWordGroup')}</button>
      </div>
    );
  }

  if (loading) return (
    <div style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '70vh',
      width: '100%',
      flexDirection: 'column',
    }}>
      <Spinner message={t('generatingSentences')} size="large" />
    </div>
  );
  if (error) return (
    <div className="card error-message">
      <h2>{t('error')}</h2>
      <p>{error}</p>
      {error.includes('Want to learn more?') && (
        <div style={{ marginTop: '0.8rem', textAlign: 'center' }}>
          <Link
            to="/store"
            className="btn btn-primary"
            style={{
              display: 'inline-block',
              textDecoration: 'none',
              background: 'linear-gradient(90deg, #a8e063 0%, #56ab2f 100%)',
              color: '#fff',
              padding: '8px 16px',
              borderRadius: '6px',
              border: 'none',
              fontSize: '0.9rem',
              fontWeight: '500'
            }}
          >
            {t('upgradeToPremium')}
          </Link>
        </div>
      )}
      <button onClick={handlePracticeAgain} className="btn btn-primary" style={{ marginTop: '1rem' }}>{t('tryAgain')}</button>
    </div>
  );

  return (
    <div className="sentence-practice-container">
      <button onClick={() => navigate(`/module/${moduleData.id}/group/${groupData.id}`)} className="btn btn-secondary btn-back">
        ← {t('backToWordGroup')}
      </button>
      <form onSubmit={handleSubmit}>
        <ol className="sentence-list">
          {(sentences || []).map((sentence, index) => {
            const parts = sentence.sentence_with_blank.split('____');
            const beforeText = parts[0];
            const afterText = parts[1] || '';

            // Check if this is an RTL language
            const targetLangCode = getKeyboardLangCode(moduleData.to_lang);
            const sourceLangCode = getKeyboardLangCode(moduleData.from_lang);
            const isTargetRTL = isRTLanguage(targetLangCode);
            const isSourceRTL = isRTLanguage(sourceLangCode);

            // Convert punctuation to RTL versions for RTL languages
            const convertToRTLPunctuation = (text) => {
              if (!isTargetRTL) return text;
              const converted = text
                .replace(/!/g, '\u061C!')  // RTL exclamation
                .replace(/\?/g, '\u061C?')  // RTL question mark
                .replace(/\./g, '\u061C.')  // RTL period
                .replace(/,/g, '\u061C,')   // RTL comma
                .replace(/:/g, '\u061C:')   // RTL colon
                .replace(/;/g, '\u061C;');  // RTL semicolon
              return converted;
            };

            return (
              <li key={index} className={`sentence-item ${isSubmitted ? 'answered ' + feedback[index]?.status : ''}`}>
                <div className="item-content" style={isTargetRTL ? { direction: 'rtl', textAlign: 'right' } : { direction: 'ltr', textAlign: 'center' }}>
                  {/* Show Hint box */}
                  {!showHints[index] && (
                    <div
                      style={{
                        background: '#fff',
                        border: '1px solid #e0e0e0',
                        borderRadius: '6px',
                        padding: '0 1.1rem',
                        marginBottom: '1rem',
                        display: 'inline-block',
                        textAlign: isSourceRTL ? 'right' : 'left',
                        lineHeight: 0.95,
                        transition: 'background 0.18s',
                      }}
                      onMouseEnter={e => { e.currentTarget.style.background = '#f3f3f3'; }}
                      onMouseLeave={e => { e.currentTarget.style.background = '#fff'; }}
                    >
                      <button
                        type="button"
                        style={{
                          background: 'none',
                          border: 'none',
                          color: '#666',
                          fontSize: '0.97rem',
                          padding: 0,
                          margin: 0,
                          fontWeight: 400,
                          cursor: 'pointer',
                          outline: 'none',
                        }}
                        onClick={() => {
                          const newShowHints = [...showHints];
                          newShowHints[index] = true;
                          setShowHints(newShowHints);
                        }}
                      >
                        {t('showHint')}
                      </button>
                    </div>
                  )}
                  {showHints[index] && (
                    <p className="translation-hint" style={isSourceRTL ? { direction: 'rtl', textAlign: 'right' } : { direction: 'ltr', textAlign: 'center' }}>
                      {t('hint')}: "{sentence.translation}"
                    </p>
                  )}
                  <label
                    className="sentence-text"
                    htmlFor={`sentence-${index}`}
                    style={isTargetRTL ? { direction: 'rtl', textAlign: 'right' } : { direction: 'ltr', textAlign: 'center' }}
                  >
                    {index + 1}. {beforeText}
                    <span className={`input-with-keyboard-icon ${focusedInputIdx === index && wrappedInputs.has(index) ? 'keyboard-active' : ''}`} style={{
                      display: 'inline-block',
                      position: 'relative',
                      verticalAlign: 'middle'
                    }}>
                      <input
                        id={`sentence-${index}`}
                        type="text"
                        value={userInputs[index]}
                        onChange={(e) => handleInputChange(index, e.target.value)}
                        className="inline-sentence-input"
                        disabled={isSubmitted}
                        style={{
                          width: `${Math.max(sentence.correct_word.length, userInputs[index].length, 5) * 1.1}ch`,
                          direction: isTargetRTL ? 'rtl' : 'ltr',
                          textAlign: 'center',
                          paddingRight: '0.2rem',
                          paddingLeft: '0.2rem',
                        }}
                        ref={el => sentenceInputRefs.current[index] = el}
                        onFocus={() => {
                          setFocusedInputIdx(index);
                          if (keyboardArmedLang === getKeyboardLangCode(moduleData.to_lang)) {
                            setKeyboardActiveIdx(index);
                          }
                          // Check if input is wrapped after a small delay
                          setTimeout(() => checkIfInputWrapped(index), 50);
                        }}
                        onBlur={e => {
                          setFocusedInputIdx(null);
                          setTimeout(() => {
                            const active = document.activeElement;
                            const isKeyboardKey = active && (
                              active.classList.contains('keyboard-key') ||
                              active.closest('.on-screen-keyboard-overlay')
                            );
                            const isSameLang = sentenceInputRefs.current.some((input, i) =>
                              input === active && getKeyboardLangCode(moduleData.to_lang) === keyboardArmedLang
                            );
                            if (!isKeyboardKey && !isSameLang) {
                              setKeyboardActiveIdx(null);
                            }
                          }, 100);
                        }}
                        autoComplete="off"
                      />
                      {focusedInputIdx === index && (
                        <div style={{
                          position: 'absolute',
                          left: 0,
                          right: 0,
                          top: '100%',
                          marginTop: '-0.35rem',
                          textAlign: 'center',
                          zIndex: 10
                        }}>
                          <button
                            type="button"
                            className="btn-toggle-keyboard subtle-toggle"
                            tabIndex={-1}
                            style={{
                              fontSize: '1.1rem',
                              background: 'none',
                              border: 'none',
                              color: '#888',
                              padding: '0.15rem 0.5rem',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              transition: 'background 0.2s',
                              lineHeight: 1,
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              height: '1.8rem',
                              width: '1.8rem',
                            }}
                            aria-label={t('showKeyboard')}
                            onMouseDown={e => {
                              e.preventDefault();
                              e.stopPropagation();
                              const langCode = getKeyboardLangCode(moduleData.to_lang);
                              if (keyboardArmedLang === langCode) {
                                setKeyboardArmedLang(null);
                                setKeyboardActiveIdx(null);
                              } else {
                                setKeyboardArmedLang(langCode);
                                setKeyboardActiveIdx(index);
                                sentenceInputRefs.current[index]?.focus();
                              }
                            }}
                          >
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <rect x="3" y="5" width="18" height="14" rx="2" />
                              <line x1="7" y1="15" x2="7" y2="15" />
                              <line x1="11" y1="15" x2="11" y2="15" />
                              <line x1="15" y1="15" x2="15" y2="15" />
                              <line x1="7" y1="11" x2="7" y2="11" />
                              <line x1="11" y1="11" x2="11" y2="11" />
                              <line x1="15" y1="11" x2="15" y2="11" />
                            </svg>
                          </button>
                        </div>
                      )}
                    </span>
                    {convertToRTLPunctuation(afterText)}
                  </label>
                  {isSubmitted && feedback[index] && (
                    <p className={`feedback-text ${feedback[index].status}`} style={{ direction: isSourceRTL ? 'rtl' : 'ltr', textAlign: isSourceRTL ? 'right' : 'center' }}>
                      {feedback[index].message}
                    </p>
                  )}
                </div>
              </li>
            );
          })}
        </ol>
        <div className="form-actions">
          {!isSubmitted ? (
            <button type="submit" className="btn btn-primary">{t('checkAnswers')}</button>
          ) : (
            <button type="button" onClick={handlePracticeAgain} className="btn btn-primary">{t('practiceAgainWithNewSentences')}</button>
          )}
        </div>
      </form>

      {keyboardArmedLang && keyboardActiveIdx !== null && (
        <div ref={keyboardOverlayRef}>
          <OnScreenKeyboard
            languageCode={keyboardArmedLang}
            onInsert={(char) => {
              const input = sentenceInputRefs.current[keyboardActiveIdx];
              if (input) {
                const start = input.selectionStart;
                const end = input.selectionEnd;
                const currentValue = input.value;

                let newValue;
                if (char === 'backspace') {
                  if (start === end) {
                    newValue = currentValue.slice(0, start - 1) + currentValue.slice(end);
                    input.setSelectionRange(start - 1, start - 1);
                  } else {
                    newValue = currentValue.slice(0, start) + currentValue.slice(end);
                    input.setSelectionRange(start, start);
                  }
                } else if (char === 'space') {
                  newValue = currentValue.slice(0, start) + ' ' + currentValue.slice(end);
                  input.setSelectionRange(start + 1, start + 1);
                } else {
                  newValue = currentValue.slice(0, start) + char + currentValue.slice(end);
                  input.setSelectionRange(start + char.length, start + char.length);
                }

                // Update React state
                handleInputChange(keyboardActiveIdx, newValue);

                // Keep focus on the input
                input.focus();
              }
            }}
            onClose={() => {
              setKeyboardArmedLang(null);
              setKeyboardActiveIdx(null);
            }}
          />
        </div>
      )}
    </div>
  );
};

export default SentencePracticePage; 